import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Redirect, Route } from "react-router-dom";

import { getSessionPortal, isAuth } from "../utils/Auth";
import {
  AUTH_CONTEXT_TYPE,
  isClientPortalAppPath,
  isCompanyPortalAppPath,
  resolvePortalLoginPath,
} from "../utils/authPortal";
import {
  clientRequiresCompanySelection,
  getClientPortalLogin,
  getClientMustChangePasswordLockRedirect,
  isAllowedPathWhileClientMustChangePassword,
  isClientMustChangePasswordLocked,
} from "../utils/clientPortal";
import {
  getCompanyLoginPostAuthRoute,
  getCompanyMustChangePasswordLockRedirect,
  isAllowedPathWhileCompanyMustChangePassword,
  isCompanyMustChangePasswordLocked,
  mustCompleteCompanyRegistration,
} from "../utils/companyRegistration";
import {
  ADMIN_PREFIX,
  CLIENT_PREFIX,
  COMPANY_PREFIX,
  ROUTE_PATHS,
} from "../utils/RoutesPaths";
import {
  canAdminModule,
  canCompanyModule,
  getAdminPermissionsHomeRoute,
  getCompanyPortalHomeRoute,
  isAdmin,
  isCompanyOwner,
  isSuperAdmin,
  isSupervisorManger,
  isUser,
  isVisitor,
} from "../utils/Permissions";

const resolveLoginPathForRequestedPortal = ({
  pathname,
  sessionPortal,
}) =>
  resolvePortalLoginPath({
    sessionPortal,
    pathname,
  });

function createPrivateRouteRenderer(
  Component,
  permission,
  companyModules,
  companyModuleFlag,
  adminModules,
  adminModuleFlag,
  companyOwnerOnly,
) {
  function PrivateRouteRenderer(props) {
    const { location } = props;
    if (!isAuth()) {
      /** Prefer matching portal login when the requested URL belongs to a portal app. */
      const loginPath = resolveLoginPathForRequestedPortal({
        pathname: location.pathname,
        sessionPortal: getSessionPortal(),
      });
      return <Redirect to={{ pathname: loginPath }} />;
    }
    const portal = getSessionPortal();
    const isAdminPath = location.pathname.startsWith(ADMIN_PREFIX);
    const isCompanyPath = location.pathname.startsWith(`${COMPANY_PREFIX}/`);
    const isClientPath = location.pathname.startsWith(`${CLIENT_PREFIX}/`);
    /** Company portal — unchanged from epic (redirect to company home, not dashboard). */
    if (portal === AUTH_CONTEXT_TYPE.COMPANY && isAdminPath) {
      return <Redirect to={{ pathname: getCompanyPortalHomeRoute() }} />;
    }
    if (portal === AUTH_CONTEXT_TYPE.COMPANY && isClientPath) {
      return <Redirect to={{ pathname: getCompanyPortalHomeRoute() }} />;
    }
    if (
      portal !== AUTH_CONTEXT_TYPE.COMPANY &&
      isCompanyPortalAppPath(location.pathname)
    ) {
      return <Redirect to={{ pathname: ROUTE_PATHS.events }} />;
    }
    /** Client portal — additive only. */
    if (
      portal === AUTH_CONTEXT_TYPE.CLIENT &&
      isClientMustChangePasswordLocked() &&
      !isAllowedPathWhileClientMustChangePassword(location.pathname)
    ) {
      return (
        <Redirect
          push={false}
          to={getClientMustChangePasswordLockRedirect()}
        />
      );
    }
    if (
      portal === AUTH_CONTEXT_TYPE.CLIENT &&
      clientRequiresCompanySelection(getClientPortalLogin())
    ) {
      return (
        <Redirect to={{ pathname: ROUTE_PATHS.clientSelectCompany }} />
      );
    }
    if (portal === AUTH_CONTEXT_TYPE.CLIENT && (isAdminPath || isCompanyPath)) {
      return <Redirect to={{ pathname: ROUTE_PATHS.clientPortalDashboard }} />;
    }
    if (
      portal !== AUTH_CONTEXT_TYPE.CLIENT &&
      isClientPortalAppPath(location.pathname)
    ) {
      return <Redirect to={{ pathname: ROUTE_PATHS.events }} />;
    }
    if (
      getSessionPortal() === AUTH_CONTEXT_TYPE.COMPANY &&
      isCompanyMustChangePasswordLocked() &&
      !isAllowedPathWhileCompanyMustChangePassword(location.pathname)
    ) {
      return (
        <Redirect
          push={false}
          to={getCompanyMustChangePasswordLockRedirect()}
        />
      );
    }
    if (
      getSessionPortal() === AUTH_CONTEXT_TYPE.COMPANY &&
      mustCompleteCompanyRegistration()
    ) {
      const target = getCompanyLoginPostAuthRoute();
      if (location.pathname !== target) {
        return <Redirect to={{ pathname: target }} />;
      }
    }
    /** Company employees without the required module flag land on Events (home). */
    if (portal === AUTH_CONTEXT_TYPE.COMPANY && companyModules?.length) {
      const flag = companyModuleFlag || "can_view";
      const hasAccess = companyModules.some((moduleName) =>
        canCompanyModule(moduleName, flag),
      );
      if (!hasAccess) {
        return (
          <Redirect to={{ pathname: getCompanyPortalHomeRoute() }} />
        );
      }
    }
    /** Owner-only pages (Dashboard, Activity Log — their APIs are owner-only). */
    if (
      portal === AUTH_CONTEXT_TYPE.COMPANY &&
      companyOwnerOnly &&
      !isCompanyOwner()
    ) {
      return <Redirect to={{ pathname: getCompanyPortalHomeRoute() }} />;
    }
    /** Visitors/supervisors without the required module flag land on their
     * first viewable route (or /no-permission). Super admin bypasses. */
    if (
      adminModules?.length &&
      !isSuperAdmin() &&
      (isVisitor() || isSupervisorManger())
    ) {
      const flag = adminModuleFlag || "can_view";
      const hasAccess = adminModules.some((moduleName) =>
        canAdminModule(moduleName, flag),
      );
      if (!hasAccess) {
        return (
          <Redirect to={{ pathname: getAdminPermissionsHomeRoute() }} />
        );
      }
    }
    if (
      permission ||
      isAdmin() ||
      isVisitor() ||
      isUser() ||
      isSupervisorManger() ||
      (portal === AUTH_CONTEXT_TYPE.COMPANY &&
        isCompanyPortalAppPath(location.pathname)) ||
      (portal === AUTH_CONTEXT_TYPE.CLIENT &&
        isClientPortalAppPath(location.pathname))
    ) {
      return <Component {...props} />;
    }
    return <Redirect to={{ pathname: ROUTE_PATHS.events }} />;
  }

  const name = Component.displayName || Component.name || "Component";
  PrivateRouteRenderer.displayName = `PrivateRoute(${name})`;

  PrivateRouteRenderer.propTypes = {
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    staticContext: PropTypes.object,
  };

  return PrivateRouteRenderer;
}

const PrivateRoute = ({
  component: Component,
  permission,
  companyModules,
  companyModuleFlag,
  adminModules,
  adminModuleFlag,
  companyOwnerOnly,
  ...rest
}) => {
  const Renderer = useMemo(
    () =>
      createPrivateRouteRenderer(
        Component,
        permission,
        companyModules,
        companyModuleFlag,
        adminModules,
        adminModuleFlag,
        companyOwnerOnly,
      ),
    [
      Component,
      permission,
      companyModules,
      companyModuleFlag,
      adminModules,
      adminModuleFlag,
      companyOwnerOnly,
    ],
  );

  return <Route {...rest} render={(props) => <Renderer {...props} />} />;
};

PrivateRoute.propTypes = {
  component: PropTypes.func,
  permission: PropTypes.any,
  /** Company permission module names; at least one must satisfy `companyModuleFlag`. */
  companyModules: PropTypes.arrayOf(PropTypes.string),
  companyModuleFlag: PropTypes.oneOf([
    "can_view",
    "can_add",
    "can_edit",
    "can_delete",
  ]),
  /** Admin permission module names; at least one must satisfy `adminModuleFlag`. */
  adminModules: PropTypes.arrayOf(PropTypes.string),
  adminModuleFlag: PropTypes.oneOf([
    "can_view",
    "can_add",
    "can_edit",
    "can_delete",
  ]),
  /** Company portal: only the company owner may open it; others go to their home. */
  companyOwnerOnly: PropTypes.bool,
};

export default PrivateRoute;
